import type { CaptureResult } from 'posthog-js';

export const OPPORTUNITY_ID_PROPERTY = 'opportunity_id';

const SALESFORCE_OPPORTUNITY_ID_PATTERN = /^006[a-zA-Z0-9]{12}(?:[a-zA-Z0-9]{3})?$/;

export type PostHogSuperPropertyClient = {
  register: (properties: Record<string, unknown>) => void;
  unregister: (property: string) => void;
};

/**
 * RED analytics joins on the case-insensitive 15-character Salesforce ID.
 * Reject anything other than a complete Opportunity ID before it reaches PostHog.
 */
export function canonicalizeOpportunityId(value: unknown): string | null {
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!SALESFORCE_OPPORTUNITY_ID_PATTERN.test(trimmed)) return null;

  return trimmed.slice(0, 15).toUpperCase();
}

export function getOpportunityIdFromPathname(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  const candidate = segments[0] === 'rep' ? segments[2] : segments[0];
  return canonicalizeOpportunityId(candidate);
}

export function syncOpportunityPropertyOnAutomaticEvent(
  event: CaptureResult | null,
): CaptureResult | null {
  if (
    !event?.properties ||
    (event.event !== '$pageview' && event.event !== '$pageleave')
  ) {
    return event;
  }

  const pathname = event.properties.$pathname;
  const canonicalId =
    typeof pathname === 'string' ? getOpportunityIdFromPathname(pathname) : null;

  if (canonicalId) {
    event.properties[OPPORTUNITY_ID_PROPERTY] = canonicalId;
  } else {
    delete event.properties[OPPORTUNITY_ID_PROPERTY];
  }

  return event;
}

/** Keep the persisted property synchronized without allowing stale or malformed IDs. */
export function syncOpportunitySuperProperty(
  posthog: PostHogSuperPropertyClient,
  opportunityId: unknown,
): string | null {
  const canonicalId = canonicalizeOpportunityId(opportunityId);

  try {
    if (canonicalId) {
      posthog.register({ [OPPORTUNITY_ID_PROPERTY]: canonicalId });
    } else {
      posthog.unregister(OPPORTUNITY_ID_PROPERTY);
    }
  } catch {
    // Analytics is best effort and must never block navigation.
  }

  return canonicalId;
}

export function clearOpportunitySuperProperty(posthog: PostHogSuperPropertyClient): void {
  try {
    posthog.unregister(OPPORTUNITY_ID_PROPERTY);
  } catch {
    // Analytics is best effort and must never block navigation.
  }
}
